import React, { useState, useEffect } from 'react';
import { Popconfirm, Tag, Table, Form, Input, Select, Button, Space, Card, Col, message } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import FilterPanel from '../../../components/common/FilterPanel';
import EllipsisCell from '@/components/common/EllipsisCell';
import { useTranslation } from 'react-i18next';
import * as storeService from '../../../services/master-data/store/storeService';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';

const StoreManagement = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
    });
    const { t } = useTranslation();
    const { tableWrapRef, scrollY } = useTableScrollHeight();

    const columns = [
        {
            title: t('warehouse.code'),
            dataIndex: 'storeCode',
            key: 'storeCode',
            width: 120,
            render: (text) => <EllipsisCell>{text}</EllipsisCell>,
        },
        {
            title: t('warehouse.name'),
            dataIndex: 'storeName',
            key: 'storeName',
            width: 200,
            render: (text) => <EllipsisCell>{text}</EllipsisCell>,
        },
        {
            title: t('warehouse.englishName'),
            dataIndex: 'englishName',
            key: 'englishName',
            width: 160,
            render: (text) => <EllipsisCell>{text}</EllipsisCell>,
        },
        {
            title: t('warehouse.state'),
            dataIndex: 'state',
            key: 'state',
            width: 100,
            render: (text) => <EllipsisCell>{text}</EllipsisCell>,
        },
        {
            title: t('warehouse.city'),
            dataIndex: 'city',
            key: 'city',
            width: 100,
            render: (text) => <EllipsisCell>{text}</EllipsisCell>,
        },
        {
            title: t('warehouse.address'),
            dataIndex: 'address',
            key: 'address',
            width: 200,
            render: (address) => <EllipsisCell>{address || '-'}</EllipsisCell>,
        },
        {
            title: t('warehouse.area'),
            dataIndex: 'area',
            key: 'area',
            width: 100,
            render: (area) => <EllipsisCell>{area || '-'}</EllipsisCell>,
        },
        {
            title: t('warehouse.format'),
            dataIndex: 'storeFormat',
            key: 'storeFormat',
            width: 100,
            render: (storeFormat) => (
                <Tag>
                    {storeFormat}
                </Tag>
            ),
        },
        {
            title: t('warehouse.status'),
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => {
                const colorMap = {
                    'ACTIVE': 'green',
                    'INACTIVE': 'red'
                };
                const textMap = {
                    'ACTIVE': t('warehouse.active'),
                    'INACTIVE': t('warehouse.unactive')
                };
                return (
                    <Tag color={colorMap[status]}>
                        {textMap[status] || status}
                    </Tag>
                );
            },
        },
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const searchParams = {
                ...form.getFieldsValue(),
                currentPage: pagination.current,
                pageSize: pagination.pageSize,
                ...params
            };

            const response = await storeService.pageStores(searchParams);

            if (response.success) {
                setDataSource(response.data.tableData);
                setPagination(prev => ({
                    ...prev,
                    current: response.data.paging?.currentPage || 1,
                    total: response.data.paging?.totalCount || 0
                }));
            } else {
                message.error(response.message || t('warehouse.queryFailed'));
            }
        } catch (error) {
            console.error('查询失败:', error);
            message.error(t('warehouse.queryFailedRetry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (values) => {
        form.setFieldsValue(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            ...pagination,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        };
        setPagination(newPagination);
        fetchData({
            currentPage: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        });
    };

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const params = form.getFieldsValue();
            const response = await storeService.exportStores(params);
            if (response.success && response.data) {
                const link = document.createElement('a');
                link.href = response.data;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                message.success(t('common.message.exportSuccess'));
            } else {
                message.error(response.errorMessage || t('common.message.exportFailed'));
            }
        } catch (error) {
            console.error('Export failed:', error);
            message.error(t('common.message.exportFailed'));
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className="store-management-container" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <FilterPanel
                form={form}
                onFilter={handleSearch}
                onReset={handleReset}
                title={t('menu.warehouse')}
                initialValues={{}}
                collapsible={true}
                defaultCollapsed={false}
                loading={loading}
            >
                <Col span={6}>
                    <Form.Item name="code" label={t('warehouse.code')}>
                        <Input placeholder={t('warehouse.codePlaceholder')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="name" label={t('warehouse.name')}>
                        <Input placeholder={t('warehouse.namePlaceholder')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="clusterSection" label={t('warehouse.format')}>
                        <Select
                            placeholder={t('warehouse.formatPlaceholder')}
                            allowClear
                            options={[
                                { label: 'GF', value: 'GF' },
                                { label: 'SUPER', value: 'SUPER' },
                                { label: 'HYPER', value: 'HYPER' },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="status" label={t('warehouse.status')}>
                        <Select
                            placeholder={t('warehouse.statusPlaceholder')}
                            allowClear
                            options={[
                                { label: t('warehouse.active'), value: 'ACTIVE' },
                                { label: t('warehouse.unactive'), value: 'INACTIVE' },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </FilterPanel>

            <Card className="store-management-table" style={{ flex: 1, overflow: 'hidden', marginTop: 16 }} bodyStyle={{ overflow: 'hidden', padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* 表格工具栏 */}
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8, borderBottom: '1px solid #f0f0f0' }}>
                    <Button icon={<ExportOutlined />} loading={exportLoading} onClick={handleExport}>
                        {t('common.export')}
                    </Button>
                </div>
                <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        rowKey="id"
                        scroll={{ x: 1200, y: scrollY }}
                    />
                </div>
            </Card>
        </div>
    );
};

export default StoreManagement;
